import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { supabase } from '../../services/supabaseClient';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface AffectedUser {
  created_by: string;
  display_name: string;
  error_count: number;
  flow_count: number;
  last_error: string;
  most_problematic_flow: string;
  most_problematic_flow_id?: string;
} 

interface UserFlowError {
  flow_id: string;
  flow_name: string;
  error_count: number;
  last_error: string;
}

interface MostAffectedUsersEnhancedProps {
  orgId: string;
  onFlowClick?: (flowId: string) => void;
  className?: string;
  initialExpanded?: boolean;
}

export interface MostAffectedUsersEnhancedRef {
  expand: () => void;
  collapse: () => void;
}

const MostAffectedUsersEnhanced = forwardRef<MostAffectedUsersEnhancedRef, MostAffectedUsersEnhancedProps>(({ 
  orgId, 
  onFlowClick,
  className = '',
  initialExpanded = false
}, ref) => {
  const [users, setUsers] = useState<AffectedUser[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [userFlows, setUserFlows] = useState<Record<string, UserFlowError[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadingUser, setLoadingUser] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    expand: () => setIsCollapsed(false),
    collapse: () => setIsCollapsed(true)
  }));

  useEffect(() => {
    if (orgId) {
      fetchAffectedUsers();
    }
  }, [orgId]);

  const fetchAffectedUsers = async () => {
    try {
      setLoading(true);
      
      // Use RPC function only - no fallback
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_most_affected_users', {
        p_org_id: orgId,
        p_days: 14  // Show last 2 weeks to match Salesforce retention
      });

      if (rpcError) {
        console.error('RPC error:', rpcError);
        setUsers([]);
        return;
      }

      setUsers(rpcData || []);

    } catch (error) {
      console.error('Error:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserFlows = async (userId: string) => {
    try {
      setLoadingUser(userId);
      
      // Use RPC function only - no fallback
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_user_flow_errors', {
        p_org_id: orgId,
        p_user_id: userId,
        p_days: 14  // Match the same time period as the main query
      });

      if (rpcError) {
        console.error('RPC error for user flows:', rpcError);
        setUserFlows(prev => ({
          ...prev,
          [userId]: []
        }));
        return;
      }

      setUserFlows(prev => ({
        ...prev,
        [userId]: rpcData || []
      }));
    } catch (error) {
      console.error('Error:', error);
      setUserFlows(prev => ({
        ...prev,
        [userId]: []
      }));
    } finally {
      setLoadingUser(null);
    }
  };

  const toggleUserExpansion = async (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
      // Fetch user flows if not already loaded
      if (!userFlows[userId]) {
        await fetchUserFlows(userId);
      }
    }
    
    setExpandedUsers(newExpanded);
  };

  const handleFlowClick = (flowId: string) => {
    if (onFlowClick) {
      onFlowClick(flowId);
    } else {
      navigate(`/${orgId}/flow/${flowId}`);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Most Affected Users</h3>
            <p className="text-sm text-gray-500">Users experiencing the most flow errors</p>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {isCollapsed ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </div>

        {!isCollapsed && (
          <div className="space-y-3">
            {users.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No flow errors found in the last 30 days
              </p>
            ) : (
              users.map((user) => (
                <div key={user.created_by} className="border rounded-lg">
                  <div
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleUserExpansion(user.created_by)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">
                            {user.display_name}
                          </span>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-red-600 font-medium">
                              {user.error_count} errors
                            </span>
                            <span className="text-gray-500">
                              {user.flow_count} flows
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleUserExpansion(user.created_by);
                              }}
                              className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
                            >
                              View Flows
                              {expandedUsers.has(user.created_by) ? (
                                <ChevronDownIcon className="ml-1 h-4 w-4" />
                              ) : (
                                <ChevronUpIcon className="ml-1 h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Last error: {formatDistanceToNow(new Date(user.last_error), { addSuffix: true })}
                          {user.most_problematic_flow && (
                            <> • Most problematic: {user.most_problematic_flow}</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {expandedUsers.has(user.created_by) && (
                    <div className="border-t bg-gray-50 px-4 py-2">
                      {loadingUser === user.created_by ? (
                        <div className="py-2 text-center">
                          <div className="inline-flex items-center text-sm text-gray-500">
                            <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading flows...
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {userFlows[user.created_by]?.map((flow) => (
                            <div
                              key={flow.flow_id}
                              className="flex items-center justify-between py-2 hover:bg-gray-100 px-2 rounded cursor-pointer"
                              onClick={() => handleFlowClick(flow.flow_id)}
                            >
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700">
                                  {flow.flow_name}
                                </span>
                                <span className="ml-2 text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(flow.last_error), { addSuffix: true })}
                                </span>
                                <div className="text-xs text-gray-600 mt-1">
                                  {flow.error_count} {flow.error_count === 1 ? 'error' : 'errors'}
                                </div>
                              </div>
                              <div className="flex items-center text-blue-600 hover:text-blue-700">
                                <span className="text-sm">View Details</span>
                                <span className="ml-1">→</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
});

MostAffectedUsersEnhanced.displayName = 'MostAffectedUsersEnhanced';

export default MostAffectedUsersEnhanced;